import React, { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import Navbar from "./components/Navbar";
import Tiles from "./components/Tiles";
import Info from "./components/Info";
import About from "./pages/About";
import CookieConsent from "react-cookie-consent";
import axios from "axios";
import DataPrivacy from "./pages/DataPrivacy";
import Imprint from "./pages/Imprint";
import RadioPlayer from "./radio/RadioPlayer";

function App() {
  const [podcasts, setPodcasts] = useState([]);
  const [mostRecentPodcast, setMostRecentPodcast] = useState(null);
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [filteredPodcasts, setFilteredPodcasts] = useState([]);

  useEffect(() => {
    const fetchPodcasts = async () => {
      try {
        // Fetch data from the backend endpoint
        // const mixcloudResponse = await axios.get("http://localhost:5001/");
        const mixcloudResponse = await axios.get(
          "https://radiocircolo.onrender.com"
        );
        const data = mixcloudResponse.data;

        // Set the combined data in state
        setPodcasts(data);

        // Set the most recent podcast to the URL of the first podcast
        if (data.length > 0) {
          setMostRecentPodcast(data[0].audio);
        }
      } catch (error) {
        console.error("Error fetching podcasts:", error);
      }
    };

    fetchPodcasts();
  }, []);

  useEffect(() => {
    // Add event listener for beforeunload
    const handleBeforeUnload = (event) => {
      // Check if audio is playing
      if (mostRecentPodcast !== null) {
        // Prevent default behavior of the event
        event.preventDefault();
        // Chrome requires returnValue to be set
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [mostRecentPodcast]);

  // Radio e Mixcloud non suonano mai insieme: quando parte uno, l'altro va in
  // pausa. Il widget Mixcloud si ricrea a ogni caricamento dell'iframe.
  const radioRef = useRef(null);
  const mixcloudIframeRef = useRef(null);
  const mixcloudWidgetRef = useRef(null);

  // Ultimo player avviato ("radio" | "mixcloud"): è quello che la barra
  // spaziatrice mette in play/pausa.
  const activePlayerRef = useRef(null);

  const handleMixcloudLoad = () => {
    mixcloudWidgetRef.current = null;
    if (!window.Mixcloud) return;
    const widget = window.Mixcloud.PlayerWidget(mixcloudIframeRef.current);
    widget.ready.then(() => {
      mixcloudWidgetRef.current = widget;
      widget.events.play.on(() => {
        activePlayerRef.current = "mixcloud";
        radioRef.current?.pause();
      });
    });
  };

  const handleRadioPlay = useCallback(() => {
    activePlayerRef.current = "radio";
    mixcloudWidgetRef.current?.pause();
  }, []);

  useEffect(() => {
    const isTyping = (el) =>
      el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);

    const handleKeyDown = (event) => {
      if (event.code !== "Space" || event.repeat || isTyping(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const active = activePlayerRef.current;
      if (!active) return;
      event.preventDefault();
      // Un pulsante con il focus verrebbe "cliccato" dallo spazio al keyup.
      if (event.target.tagName === "BUTTON") event.target.blur();
      if (active === "radio") radioRef.current?.toggle();
      else mixcloudWidgetRef.current?.togglePlay();
    };

    // Dopo un click nel widget Mixcloud i tasti finirebbero dentro l'iframe:
    // si riporta il focus sulla pagina così lo spazio continua a funzionare.
    const handleWindowBlur = () =>
      setTimeout(() => {
        if (document.activeElement === mixcloudIframeRef.current) {
          mixcloudIframeRef.current.blur();
        }
      }, 0);

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  const handleTileClick = (podcast) => {
    setSelectedPodcast(podcast);
  };

  const handleSearch = (searchTerm) => {
    const filtered = podcasts.filter((podcast) =>
      podcast.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPodcasts(filtered);
  };

  return (
    <Router basename="/">
      {/* 100dvh = altezza visibile reale sui telefoni (100vh include la barra del browser) */}
      <div className="flex flex-col h-screen h-[100dvh]">
        {/* Topbar dei player: ogni player è un blocco flex-1 */}
        <div className="sticky top-0 z-40 flex w-full shrink-0">
          <RadioPlayer ref={radioRef} onPlay={handleRadioPlay} />
        </div>
        <Navbar onSearch={handleSearch} />
        <div className="flex-grow overflow-y-auto">
          {/* Cookie Consent Banner */}
          <CookieConsent
            location="bottom"
            buttonText="I Accept"
            cookieName="myCookieConsent"
            style={{ background: "#2B373B" }}
            buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
            expires={150}
          >
            This website uses cookies to enhance the user experience.{" "}
            <span style={{ fontSize: "10px" }}>
              For more information, read our{" "}
              <Link to="/data-privacy" style={{ color: "#e66767" }}>
                Privacy Policy
              </Link>
              .
            </span>
          </CookieConsent>
          {/* End of Cookie Consent Banner */}
          <div className="flex flex-col flex-grow-1">
            <Routes>
              {/* Homepage with list of podcasts */}
              <Route
                path="/"
                element={
                  <Tiles
                    podcasts={
                      filteredPodcasts.length ? filteredPodcasts : podcasts
                    }
                    onTileClicked={handleTileClick}
                    selectedPodcast={selectedPodcast}
                    setSelectedPodcast={setSelectedPodcast}
                  />
                }
              />
              <Route path="/about" element={<About />} />
              <Route path="/data-privacy" element={<DataPrivacy />} />
              <Route path="/imprint" element={<Imprint />} />
              {/* Podcast information page */}
              <Route
                path="/:slug"
                element={
                  <Info
                    podcast={selectedPodcast}
                    podcasts={podcasts}
                    setMostRecentPodcast={setMostRecentPodcast}
                    setSelectedPodcast={setSelectedPodcast}
                  />
                }
              />
            </Routes>
          </div>
          {/* Add the audio element for the most recent podcast */}
          {mostRecentPodcast && (
            <div className="fixed bottom-0 mx-10 w-full">
              {/* ml-0 xs:ml-4  */}

              <iframe
                ref={mixcloudIframeRef}
                onLoad={handleMixcloudLoad}
                title="Mixcloud Player"
                style={{ width: 'calc(100% - 5rem)' }} 
                height="60"
                src={`https://www.mixcloud.com/widget/iframe/?hide_cover=1&mini=1&autoplay==1&mute=1&feed=${encodeURIComponent(
                  mostRecentPodcast
                )}`}
                frameBorder="0"
              />
            </div>
          )}
        </div>
      </div>
    </Router>
  );
}

export default App;
